import React, { useEffect, useState } from 'react';
import { Box, CloudinaryImage, Button, Inline, RichText, Text, IconSocialApple16 } from '@stitch-fix/mode-react';
import Image from 'next/image';
import { SupportedBusinessLine } from '../Onboarding';
import { baseUrl } from '../constants';
import { getClientEnvironment, DeviceEnv } from './getClientEnvironment';
import iOSAppStoreQrCode from '../../../../../public/iOS_App_Store.svg';
import androidAppStoreQrCode from '../../../../../public/Android_Google_Play.svg';
import iconGooglePlay16 from '../../../../../public/Google_Play_Logo.svg';
import styles from './NotOnboardedMessage.module.scss';
const photos = {
  WOMENS: {
    mediaPath: '/v1757093696/vision/assets/womens_image_spreadout.png',
    altText: 'Three AI-styled outfit examples of a women are shown in different settings'
  },
  MENS: {
    mediaPath: '/v1757459387/vision/assets/9-2-mens-image-spread.png',
    altText: 'Three AI-styled outfit examples of a man are shown in different settings'
  }
};
const iosLink = 'https://apps.apple.com/us/app/stitch-fix-personal-styling/id1022579925?utm_source=site&utm_medium=site&utm_campaign=insert_us_all_postcheckout_brand_visionappios_120225_active';
const androidLink = 'https://play.google.com/store/apps/details?id=com.stitchfix.stitchfix&utm_source=site&utm_medium=site&utm_campaign=insert_us_all_postcheckout_brand_visionappandroid_120225_active';
type NotOnboardedMessageProps = {
  businessLine: SupportedBusinessLine;
  env?: DeviceEnv;
};
const NotOnboardedMessage = ({
  businessLine,
  env: envProp
}: NotOnboardedMessageProps) => {
  const [env, setEnv] = useState<DeviceEnv | null>(envProp ?? null);
  useEffect(() => {
    if (!envProp) {
      setEnv(getClientEnvironment());
    }
  }, [envProp]);

  // Render nothing until we know the client environment (avoids flash)
  if (!env) return null;
  let downloadSection;
  if (env === 'ios-browser' || env === 'android-browser') {
    downloadSection = <Box>
        <Text setting="body-medium" align="center">
          Available on iOS and Android
        </Text>
        <Button as="a" href={env === 'ios-browser' ? iosLink : androidLink} width="fill" target="_blank" rel="noopener noreferrer" mt={1}>
          Download the app
        </Button>
      </Box>;
  } else if (env === 'web') {
    downloadSection = <Inline spacing={1} hAlign="center">
        <Box width={{
        sm: '80px',
        md: '106px'
      }}>
          <Image src={iOSAppStoreQrCode} alt="QR code to download the iOS app" width={106} height={106} />
          <Inline spacing={0.5} mt={0.5} vAlign="center">
            <IconSocialApple16 title="Apple logo" />
            <Text setting="body-xsmall">App Store</Text>
          </Inline>
        </Box>

        <Box width={{
        sm: '80px',
        md: '106px'
      }}>
          <Image src={androidAppStoreQrCode} alt="QR code to download the Android app" width={106} height={106} />
          <Inline spacing={0.5} mt={0.5} vAlign="center">
            <Image src={iconGooglePlay16} alt="Google Play logo" width={16} height={16} />
            <Text setting="body-xsmall">Google Play</Text>
          </Inline>
        </Box>
      </Inline>;
  }
  return <Box className={styles.container}>
      <Box minWidth="270px" maxWidth="380px" width="100%" className={styles.imageContainer}>
        <CloudinaryImage baseUrl={baseUrl} mediaPath={photos[businessLine].mediaPath} alt={photos[businessLine].altText} sizes={{
        sm: 350
      }} width={350} objectFit="contain" />
      </Box>
      <Box>
        <Text setting="display-medium" color="blue-45" align={{
        sm: 'center',
        lg: 'left'
      }}>
          <RichText>Be one of the first *to try Vision*</RichText>
        </Text>
        <Text setting="body-medium" align={{
        sm: 'center',
        lg: 'left'
      }} mt={1}>
          Imagine how you could look in fresh styles with our experimental AI.
        </Text>
        <Box bgColor="gray-99" mt={2} p={1.5} style={{
        borderRadius: '8px'
      }} mx={{
        sm: 'auto',
        lg: 'initial'
      }} maxWidth={{
        sm: '380px',
        lg: 'initial'
      }}>
          <Text setting="title-small" mb={1} align="center">
            Try Vision on the app
          </Text>
          {downloadSection}
        </Box>
      </Box>
    </Box>;
};
export default NotOnboardedMessage;